import React, {useContext, useMemo} from 'react'
import AppContext from '../../appContext'
import LimeProgramLabInputs from '../../controllers/LimeProgramLabInputs'
import LimePlayFieldRow from '../../controllers/LimePlayFieldRow'
import {customUnits, unitCustom} from '../program/CustomUnits'
import {ProductNames} from './LimePlayfieldUnified'

interface LimeOutputTabProps {
    labInputs: LimeProgramLabInputs
    calculatedRows: LimePlayFieldRow[]
    products: ProductNames
}

const tableStyles = {
    headerGroup: 'whitespace-nowrap px-2 py-2 text-center text-xs font-semibold bg-gray-200 border-l-2 border-gray-100',
    headerSub: 'whitespace-nowrap px-2 py-1 text-xs font-semibold bg-gray-200 border-l-2 border-gray-100',
    stickyHeader: 'whitespace-nowrap px-2 py-2 text-left text-xs font-semibold bg-gray-200 sticky z-20',
    cell: 'whitespace-nowrap px-2 py-2 text-xs text-gray-600 border-l-2 border-b-2 border-gray-100 text-right',
    stickyCell: 'whitespace-nowrap px-2 py-2 text-xs text-gray-600 border-l-2 border-b-2 border-gray-100 sticky z-10 bg-white'
}

const outputColCount = 13

const LimeOutputTab: React.FC<LimeOutputTabProps> = ({labInputs, calculatedRows, products}) => {
    const app = useContext(AppContext)
    const system = app.initial.system
    const rateLabel = unitCustom(system, customUnits.kgha_lbAc)
    const toDisplayRate = (value: number): number =>
        system === 'METRIC' ? value : customUnits.kgha_lbAc.metric.invert(value)
    const formatRate = (value: number): string => {
        const converted = toDisplayRate(value)
        return converted > 0 ? converted.toFixed(0) : '-'
    }

    // Pair rows with calculated data and sort by reference number
    const sortedRows = useMemo(() => {
        return labInputs.rows
            .map((row, idx) => ({ row, calc: calculatedRows[idx] }))
            .filter((x): x is { row: typeof x.row, calc: LimePlayFieldRow } => !!x.calc)
            .sort((a, b) => (a.row.referenceNr || a.row.landName).localeCompare(b.row.referenceNr || b.row.landName))
    }, [labInputs, calculatedRows])

    return (
        <div className="p-4 flex-1 min-h-0 flex flex-col overflow-hidden">
            {sortedRows.length === 0 ? (
                <div className="text-gray-500 text-sm">{app.word('no_data')}</div>
            ) : (
                <div className="overflow-auto flex-1 border border-gray-200 rounded">
                    <table className="border-collapse">
                        <thead className="sticky top-0 z-30">
                            {/* Row 1: Group header */}
                            <tr>
                                <th rowSpan={2} className={`${tableStyles.stickyHeader} left-0 bg-gray-200`} style={{minWidth: '40px'}}>#</th>
                                <th rowSpan={2} className={`${tableStyles.stickyHeader} left-[40px] bg-gray-200`} style={{minWidth: '80px'}}>
                                    {app.word('land_ref')}
                                </th>
                                <th rowSpan={2} className={`${tableStyles.stickyHeader} left-[120px] bg-gray-200`} style={{minWidth: '80px'}}>
                                    {app.word('reference_nr')}
                                </th>
                                <th colSpan={outputColCount} className={tableStyles.headerGroup}>
                                    {app.word('output')}
                                </th>
                            </tr>
                            {/* Row 2: Column headers */}
                            <tr>
                                <th className={tableStyles.headerSub}>{products.calciticLime} Y1 ({rateLabel})</th>
                                <th className={tableStyles.headerSub}>{products.dolomiticLime} Y1 ({rateLabel})</th>
                                <th className={tableStyles.headerSub}>{products.calciticLime} Y2 ({rateLabel})</th>
                                <th className={tableStyles.headerSub}>{products.dolomiticLime} Y2 ({rateLabel})</th>
                                <th className={tableStyles.headerSub}>{products.calciticLime} Y3 ({rateLabel})</th>
                                <th className={tableStyles.headerSub}>{products.dolomiticLime} Y3 ({rateLabel})</th>
                                <th className={tableStyles.headerSub}>{products.phosphate} Y1 ({rateLabel})</th>
                                <th className={tableStyles.headerSub}>{products.phosphate} Y2 ({rateLabel})</th>
                                <th className={tableStyles.headerSub}>{products.potassium} Y1 ({rateLabel})</th>
                                <th className={tableStyles.headerSub}>{products.potassium} Y2 ({rateLabel})</th>
                                <th className={tableStyles.headerSub}>{products.gypsum} Y1 ({rateLabel})</th>
                                <th className={tableStyles.headerSub}>{products.gypsum} Y2 ({rateLabel})</th>
                                <th className={tableStyles.headerSub}>Elem. S ({rateLabel})</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map(({row, calc}, idx) => {
                                const isCal = calc.limeType !== 'Dolomitic'
                                return (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className={`${tableStyles.stickyCell} left-0`} style={{minWidth: '40px'}}>{idx + 1}</td>
                                        <td className={`${tableStyles.stickyCell} left-[40px]`} style={{minWidth: '80px'}}>{row.landName}</td>
                                        <td className={`${tableStyles.stickyCell} left-[120px] border-r-2 border-gray-300`} style={{minWidth: '80px'}}>{row.referenceNr}</td>
                                        <td className={tableStyles.cell}>{isCal ? formatRate(calc.appliedYear1KgHa) : '-'}</td>
                                        <td className={tableStyles.cell}>{!isCal ? formatRate(calc.appliedYear1KgHa) : '-'}</td>
                                        <td className={tableStyles.cell}>{isCal ? formatRate(calc.appliedYear2KgHa) : '-'}</td>
                                        <td className={tableStyles.cell}>{!isCal ? formatRate(calc.appliedYear2KgHa) : '-'}</td>
                                        <td className={tableStyles.cell}>{isCal ? formatRate(calc.appliedYear3KgHa) : '-'}</td>
                                        <td className={tableStyles.cell}>{!isCal ? formatRate(calc.appliedYear3KgHa) : '-'}</td>
                                        <td className={tableStyles.cell}>{formatRate(calc.pFertYear1KgHa)}</td>
                                        <td className={tableStyles.cell}>{formatRate(calc.pFertYear2KgHa)}</td>
                                        <td className={tableStyles.cell}>{formatRate(calc.kFertYear1KgHa)}</td>
                                        <td className={tableStyles.cell}>{formatRate(calc.kFertYear2KgHa)}</td>
                                        <td className={tableStyles.cell}>{formatRate(calc.gypsumYear1KgHa)}</td>
                                        <td className={tableStyles.cell}>{formatRate(calc.gypsumYear2KgHa)}</td>
                                        <td className={tableStyles.cell}>{formatRate(calc.elementalSulphurKgHa)}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}

export default LimeOutputTab
